/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useHistory } from "react-router-dom";
import { Row } from "react-table";
import { Box, Loader, Heading } from "@nordcloud/gnui";
import { QueryPagination, Table } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { FilteredSearch } from "~/components/Search/FilteredSearch";
import { ServiceCommitment } from "~/models";
import { ACTIONS, Can, ROUTES } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import useSortParams from "~/tools/hooks/useSortParams";
import { ServiceAdd } from "./ServiceAdd";
import { ServiceCatalogListColumns } from "./ServiceCatalogList";

function ServiceCatalogPage() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [serviceCatalog, setServiceCatalog] = React.useState({
    list: [],
    totalCount: 0,
  });
  const [serviceCatalogFiltered, setServiceCatalogFiltered] = React.useState(
    []
  );
  const [sortParams, setSorting] = useSortParams("name");
  const [{ limit, page, query }] = useQueryState();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchServiceCatalog = React.useCallback(async () => {
    try {
      const params = { ...sortParams, limit, page, query };
      const { data } = await Axios().get("/catalog/services", params);
      setServiceCatalog({
        list: data?.services,
        totalCount: data?.totalCount,
      });
      setServiceCatalogFiltered(data?.services);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, limit, page, query, sortParams, showError]);

  React.useEffect(() => {
    fetchServiceCatalog();
  }, [fetchServiceCatalog]);

  const columns = React.useMemo(() => ServiceCatalogListColumns, []);

  const history = useHistory();

  const handleRowKeyPress = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    selected: ServiceCommitment
  ) => {
    if (e.key === "Enter") {
      onRedirectHandler(selected);
    }
  };

  const onRedirectHandler = (row: ServiceCommitment) => {
    history.push(`${ROUTES.serviceCommitments.index}/${row.id}`);
  };

  return (
    <>
      <Box css={{ display: "flex", alignItems: "center" }} spacing="spacing04">
        <Heading level={2}>Service Catalog</Heading>
        <Can action={ACTIONS.ADD_SERVICE_COMMITMENT}>
          <ServiceAdd />
        </Can>
      </Box>
      <Box spacing="spacing04">
        <Box boxStyle="lightGrey" spacing="spacing04">
          <FilteredSearch
            mode="services"
            endpoint="services"
            redirectLink={ROUTES.serviceCommitments.index}
            options={{ keys: ["id", "name", "version"] }}
          />
        </Box>
        {isLoading ? (
          <Loader inContent />
        ) : (
          <>
            <Table
              getRowProps={(row: Row) => ({
                onClick: () =>
                  onRedirectHandler(row.original as ServiceCommitment),
                onKeyPress: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                  handleRowKeyPress(e, row.original as ServiceCommitment),
                className: "interactive",
                tabIndex: 0,
                role: "button",
              })}
              columns={columns}
              data={serviceCatalogFiltered || []}
              defaultSort={[{ id: "name", desc: false }]}
              serverSideSort={setSorting}
            />
            <QueryPagination count={serviceCatalog.totalCount} />
          </>
        )}
      </Box>
    </>
  );
}

export { ServiceCatalogPage };
